import React from 'react';
import {
  EmojiEvents as TrophyIcon,
  School as SchoolIcon,
  Psychology as PsychologyIcon,
  Whatshot as WhatshotIcon,
  Stars as StarsIcon,
  Timer as TimerIcon,
  CalendarMonth as CalendarIcon,
  AutoAwesome as AutoAwesomeIcon,
  WorkspacePremium as WorkspacePremiumIcon,
  TrendingUp as TrendingUpIcon,
  LocalFireDepartment as LocalFireDepartmentIcon,
  Grade as GradeIcon,
  Diamond as DiamondIcon,
  Bolt as BoltIcon,
  Speed as SpeedIcon,
  Functions as FunctionsIcon,
  Architecture as ArchitectureIcon,
  SportsScore as SportsScoreIcon,
  Replay as ReplayIcon
} from '@mui/icons-material';

// Icon color mapping
export const iconColors = {
  'EmojiEvents': '#FFD700', // Gold
  'School': '#4169E1', // Royal Blue
  'Psychology': '#9C27B0', // Purple
  'Whatshot': '#FF5722', // Deep Orange
  'Stars': '#FFC107', // Amber
  'Timer': '#2196F3', // Blue
  'CalendarMonth': '#4CAF50', // Green
  'AutoAwesome': '#42A5F5', // Light Blue
  'WorkspacePremium': '#673AB7', // Deep Purple
  'TrendingUp': '#00BCD4', // Cyan
  'LocalFireDepartment': '#FF9800', // Orange
  'Grade': '#CDDC39', // Lime
  'Diamond': '#9C27B0', // Purple
  'Bolt': '#FFEB3B', // Yellow
  'Speed': '#FF5252', // Red Accent
  'Functions': '#3F51B5', // Indigo
  'Architecture': '#607D8B', // Blue Grey
  'SportsScore': '#8BC34A', // Light Green
  'Replay': '#00BCD4' // Cyan
};

// Background color mapping for achievement cards
export const getBackgroundColor = (iconName?: string) => {
  if (!iconName) return 'rgba(229,234,255,1)';

  const backgroundColors: Record<string, string> = {
    'EmojiEvents': 'rgba(255,238,186,1)', // Gold
    'School': 'rgba(213,233,255,1)', // Royal Blue
    'Psychology': 'rgba(243,213,255,1)', // Purple
    'Whatshot': 'rgba(255,223,213,1)', // Deep Orange
    'Stars': 'rgba(255,249,213,1)', // Amber
    'Timer': 'rgba(213,243,255,1)', // Blue
    'CalendarMonth': 'rgba(213,255,215,1)', // Green
    'AutoAwesome': 'rgba(213,242,255,1)', // Light Blue
    'WorkspacePremium': 'rgba(233,213,255,1)', // Deep Purple
    'TrendingUp': 'rgba(213,255,253,1)', // Cyan
    'LocalFireDepartment': 'rgba(255,235,213,1)', // Orange
    'Grade': 'rgba(247,255,213,1)', // Lime
    'Diamond': 'rgba(244,213,255,1)', // Purple
    'Bolt': 'rgba(255,253,213,1)', // Yellow
    'Speed': 'rgba(255,213,213,1)', // Red
    'Functions': 'rgba(215,220,255,1)', // Indigo
    'Architecture': 'rgba(231,238,242,1)', // Blue Grey
    'SportsScore': 'rgba(236,255,213,1)', // Light Green
    'Replay': 'rgba(213,255,250,1)' // Cyan
  };

  return backgroundColors[iconName] || 'rgba(229,234,255,1)';
};

// Icon gradient background mapping
export const getIconGradient = (iconName?: string) => {
  if (!iconName) return 'radial-gradient(circle, rgba(63,81,181,0.1) 0%, rgba(63,81,181,0) 70%)';

  const gradients: Record<string, string> = {
    'EmojiEvents': 'radial-gradient(circle, rgba(255,215,0,0.1) 0%, rgba(255,215,0,0) 70%)', 
    'School': 'radial-gradient(circle, rgba(65,105,225,0.1) 0%, rgba(65,105,225,0) 70%)',
    'Psychology': 'radial-gradient(circle, rgba(156,39,176,0.1) 0%, rgba(156,39,176,0) 70%)',
    'Whatshot': 'radial-gradient(circle, rgba(255,87,34,0.1) 0%, rgba(255,87,34,0) 70%)',
    'Stars': 'radial-gradient(circle, rgba(255,193,7,0.1) 0%, rgba(255,193,7,0) 70%)',
    'Timer': 'radial-gradient(circle, rgba(33,150,243,0.1) 0%, rgba(33,150,243,0) 70%)',
    'CalendarMonth': 'radial-gradient(circle, rgba(76,175,80,0.1) 0%, rgba(76,175,80,0) 70%)',
    'AutoAwesome': 'radial-gradient(circle, rgba(66,165,245,0.1) 0%, rgba(66,165,245,0) 70%)',
    'WorkspacePremium': 'radial-gradient(circle, rgba(103,58,183,0.1) 0%, rgba(103,58,183,0) 70%)',
    'TrendingUp': 'radial-gradient(circle, rgba(0,188,212,0.1) 0%, rgba(0,188,212,0) 70%)',
    'LocalFireDepartment': 'radial-gradient(circle, rgba(255,152,0,0.1) 0%, rgba(255,152,0,0) 70%)',
    'Grade': 'radial-gradient(circle, rgba(205,220,57,0.1) 0%, rgba(205,220,57,0) 70%)',
    'Diamond': 'radial-gradient(circle, rgba(156,39,176,0.1) 0%, rgba(156,39,176,0) 70%)',
    'Bolt': 'radial-gradient(circle, rgba(255,235,59,0.1) 0%, rgba(255,235,59,0) 70%)',
    'Speed': 'radial-gradient(circle, rgba(255,82,82,0.1) 0%, rgba(255,82,82,0) 70%)',
    'Functions': 'radial-gradient(circle, rgba(63,81,181,0.1) 0%, rgba(63,81,181,0) 70%)',
    'Architecture': 'radial-gradient(circle, rgba(96,125,139,0.1) 0%, rgba(96,125,139,0) 70%)',
    'SportsScore': 'radial-gradient(circle, rgba(139,195,74,0.1) 0%, rgba(139,195,74,0) 70%)',
    'Replay': 'radial-gradient(circle, rgba(0,188,212,0.1) 0%, rgba(0,188,212,0) 70%)'
  };

  return gradients[iconName] || 'radial-gradient(circle, rgba(63,81,181,0.1) 0%, rgba(63,81,181,0) 70%)';
};

// Achievement name text color mapping
export const getTextColor = (iconName?: string) => {
  if (!iconName) return 'text.primary';

  const textColors: Record<string, string> = {
    'EmojiEvents': '#B8860B', // Gold
    'School': '#1A3A8E', // Royal Blue
    'Psychology': '#6A1B9A', // Purple
    'Whatshot': '#BF360C', // Deep Orange
    'Stars': '#B1790A', // Amber
    'Timer': '#0B579E', // Blue
    'CalendarMonth': '#1B5E20', // Green
    'AutoAwesome': '#0277BD', // Light Blue
    'WorkspacePremium': '#4527A0', // Deep Purple
    'TrendingUp': '#00838F', // Cyan
    'LocalFireDepartment': '#E65100', // Orange
    'Grade': '#827717', // Lime
    'Diamond': '#6A1B9A', // Purple
    'Bolt': '#F57F17', // Yellow
    'Speed': '#C62828', // Red
    'Functions': '#1A237E', // Indigo
    'Architecture': '#37474F', // Blue Grey
    'SportsScore': '#33691E', // Light Green
    'Replay': '#006064' // Cyan
  };

  return textColors[iconName] || 'text.primary';
};

// Helper function to get the appropriate icon based on iconName with vibrant colors
export const getIconComponent = (iconName: string | undefined) => {
  // Choose a random icon and color if none is specified
  const chooseRandomIcon = () => {
    const iconNames = Object.keys(iconColors);
    const randomIcon = iconNames[Math.floor(Math.random() * iconNames.length)];
    return {
      name: randomIcon,
      color: iconColors[randomIcon as keyof typeof iconColors]
    };
  };
  
  // Use the specified iconName or choose a random one
  const selectedIcon = iconName || chooseRandomIcon().name;
  
  const getColorForIcon = (name: string) => iconColors[name as keyof typeof iconColors] || '#3f51b5';
  
  const icons: { [key: string]: React.ReactNode } = {
    'EmojiEvents': <TrophyIcon fontSize="large" sx={{ color: getColorForIcon('EmojiEvents'), fontSize: 40 }} />,
    'School': <SchoolIcon fontSize="large" sx={{ color: getColorForIcon('School'), fontSize: 40 }} />,
    'Psychology': <PsychologyIcon fontSize="large" sx={{ color: getColorForIcon('Psychology'), fontSize: 40 }} />,
    'Whatshot': <WhatshotIcon fontSize="large" sx={{ color: getColorForIcon('Whatshot'), fontSize: 40 }} />,
    'Stars': <StarsIcon fontSize="large" sx={{ color: getColorForIcon('Stars'), fontSize: 40 }} />,
    'Timer': <TimerIcon fontSize="large" sx={{ color: getColorForIcon('Timer'), fontSize: 40 }} />,
    'CalendarMonth': <CalendarIcon fontSize="large" sx={{ color: getColorForIcon('CalendarMonth'), fontSize: 40 }} />,
    'AutoAwesome': <AutoAwesomeIcon fontSize="large" sx={{ color: getColorForIcon('AutoAwesome'), fontSize: 40 }} />,
    'WorkspacePremium': <WorkspacePremiumIcon fontSize="large" sx={{ color: getColorForIcon('WorkspacePremium'), fontSize: 40 }} />,
    'TrendingUp': <TrendingUpIcon fontSize="large" sx={{ color: getColorForIcon('TrendingUp'), fontSize: 40 }} />,
    'LocalFireDepartment': <LocalFireDepartmentIcon fontSize="large" sx={{ color: getColorForIcon('LocalFireDepartment'), fontSize: 40 }} />,
    'Grade': <GradeIcon fontSize="large" sx={{ color: getColorForIcon('Grade'), fontSize: 40 }} />,
    'Diamond': <DiamondIcon fontSize="large" sx={{ color: getColorForIcon('Diamond'), fontSize: 40 }} />,
    'Bolt': <BoltIcon fontSize="large" sx={{ color: getColorForIcon('Bolt'), fontSize: 40 }} />,
    'Speed': <SpeedIcon fontSize="large" sx={{ color: getColorForIcon('Speed'), fontSize: 40 }} />,
    'Functions': <FunctionsIcon fontSize="large" sx={{ color: getColorForIcon('Functions'), fontSize: 40 }} />,
    'Architecture': <ArchitectureIcon fontSize="large" sx={{ color: getColorForIcon('Architecture'), fontSize: 40 }} />,
    'SportsScore': <SportsScoreIcon fontSize="large" sx={{ color: getColorForIcon('SportsScore'), fontSize: 40 }} />,
    'Replay': <ReplayIcon fontSize="large" sx={{ color: getColorForIcon('Replay'), fontSize: 40 }} />
  };
  
  return icons[selectedIcon] || <TrophyIcon fontSize="large" sx={{ color: getColorForIcon('EmojiEvents'), fontSize: 40 }} />;
};

// Simple function to get achievement icon for leaderboard
export const getAchievementIcon = (iconName?: string, color?: string) => {
  const icons: { [key: string]: React.ReactNode } = {
    'EmojiEvents': <TrophyIcon sx={{ color: color || iconColors['EmojiEvents'] }} />,
    'School': <SchoolIcon sx={{ color: color || iconColors['School'] }} />,
    'Psychology': <PsychologyIcon sx={{ color: color || iconColors['Psychology'] }} />,
    'Whatshot': <WhatshotIcon sx={{ color: color || iconColors['Whatshot'] }} />,
    'Stars': <StarsIcon sx={{ color: color || iconColors['Stars'] }} />,
    'Timer': <TimerIcon sx={{ color: color || iconColors['Timer'] }} />,
    'CalendarMonth': <CalendarIcon sx={{ color: color || iconColors['CalendarMonth'] }} />,
    'AutoAwesome': <AutoAwesomeIcon sx={{ color: color || iconColors['AutoAwesome'] }} />,
    'WorkspacePremium': <WorkspacePremiumIcon sx={{ color: color || iconColors['WorkspacePremium'] }} />,
    'TrendingUp': <TrendingUpIcon sx={{ color: color || iconColors['TrendingUp'] }} />,
    'LocalFireDepartment': <LocalFireDepartmentIcon sx={{ color: color || iconColors['LocalFireDepartment'] }} />,
    'Grade': <GradeIcon sx={{ color: color || iconColors['Grade'] }} />,
    'Diamond': <DiamondIcon sx={{ color: color || iconColors['Diamond'] }} />,
    'Bolt': <BoltIcon sx={{ color: color || iconColors['Bolt'] }} />,
    'Speed': <SpeedIcon sx={{ color: color || iconColors['Speed'] }} />,
    'Functions': <FunctionsIcon sx={{ color: color || iconColors['Functions'] }} />,
    'Architecture': <ArchitectureIcon sx={{ color: color || iconColors['Architecture'] }} />,
    'SportsScore': <SportsScoreIcon sx={{ color: color || iconColors['SportsScore'] }} />,
    'Replay': <ReplayIcon sx={{ color: color || iconColors['Replay'] }} />
  };
  
  return icons[iconName || ''] || <TrophyIcon sx={{ color: color || iconColors['EmojiEvents'] }} />;
};

// Common achievement card background pattern
export const backgroundPattern = `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23000000' fill-opacity='0.1' fill-rule='evenodd'/%3E%3C/svg%3E")`;